import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import useNotification from '../hooks/useNotification';
import axiosInstance from '../api/Axios';
import { API_ENDPOINTS } from '../constants/api';
import UsersAnalyticsTable from '../components/analytics/UsersAnalyticsTable';
import { formatHours, roundToOneDecimal } from '../utils/numberUtils';
import { 
  ChartBarIcon, 
  ClockIcon, 
  CalendarIcon,
  UserIcon 
} from '@heroicons/react/24/outline';

const Analytics = () => {
  const { user } = useAuth();
  const { showError } = useNotification();
  const [monthlyHours, setMonthlyHours] = useState([]);
  const [usersAnalytics, setUsersAnalytics] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const isAdmin = user?.roles?.includes('ADMIN');

  // Отладочная информация
  console.log('Analytics - User:', user);
  console.log('Analytics - isAdmin:', isAdmin);
  console.log('Analytics - user.roles:', user?.roles);

  // Получаем данные о часах по месяцам
  const fetchMonthlyHours = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(`${API_ENDPOINTS.MONTHLY_HOURS}/${currentYear}`);
      setMonthlyHours(response.data);
    } catch (error) {
      console.error('Error fetching monthly hours:', error);
      showError('Ошибка загрузки данных аналитики');
      // Если нет данных, показываем пустые месяцы
      setMonthlyHours(generateEmptyData());
    } finally {
      setIsLoading(false);
    }
  };

  // Получаем детальную статистику по пользователям (только для админов)
  const fetchUsersAnalytics = async () => {
    console.log('fetchUsersAnalytics called, isAdmin:', isAdmin);
    if (!isAdmin) {
      console.log('fetchUsersAnalytics: not admin, returning');
      return;
    }
    
    try {
      console.log('fetchUsersAnalytics: making request to:', `${API_ENDPOINTS.USERS_ANALYTICS}/${currentYear}`);
      const response = await axiosInstance.get(`${API_ENDPOINTS.USERS_ANALYTICS}/${currentYear}`);
      console.log('fetchUsersAnalytics: response received:', response.data);
      setUsersAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching users analytics:', error);
      showError('Ошибка загрузки статистики по пользователям');
      setUsersAnalytics([]);
    }
  };

  // Генерируем пустые данные, если нет информации
  const generateEmptyData = () => {
    const months = [
      'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ];
    
    return months.map((month) => ({
      month,
      hours: 0,
      tasks: 0,
      completed: 0
    }));
  };

  useEffect(() => {
    console.log('Analytics useEffect triggered, isAdmin:', isAdmin);
    if (isAdmin) {
      console.log('Analytics: loading both monthly hours and users analytics');
      Promise.all([fetchMonthlyHours(), fetchUsersAnalytics()]);
    } else {
      console.log('Analytics: loading only monthly hours');
      fetchMonthlyHours();
    }
  }, [currentYear]);

  // Вычисляем общую статистику
  const totalHours = roundToOneDecimal(monthlyHours.reduce((sum, item) => sum + item.hours, 0));
  const totalTasks = monthlyHours.reduce((sum, item) => sum + item.tasks, 0);
  const totalCompleted = monthlyHours.reduce((sum, item) => sum + item.completed, 0);
  const averageHoursPerMonth = Math.round(totalHours / 12);

  // Находим максимальное значение для нормализации высоты столбцов
  const maxHours = Math.max(...monthlyHours.map(item => item.hours), 1);
  const maxCompleted = Math.max(...monthlyHours.map(item => item.completed), 1);

  // Генерируем список лет для селектора
  const generateYearOptions = () => {
    const currentYearNum = new Date().getFullYear();
    const years = [];
    for (let year = currentYearNum; year >= currentYearNum - 5; year--) {
      years.push(year);
    }
    return years;
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden min-h-screen bg-gray-200">
        <main className="flex-1 overflow-x-hidden overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Загрузка аналитики...</div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden min-h-screen bg-gray-200">
      <main className="flex-1 overflow-x-hidden overflow-y-auto p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-semibold text-gray-900">Аналитика</h1>
            <p className="text-gray-600">Статистика по часам и задачам</p>
            <p className="text-sm text-blue-600 mt-1">
              ⏰ Все времена отображаются в вашем локальном часовом поясе
            </p>
          </div>

          {/* Заголовок */}
          <div className="mb-8 text-center">
            <div className="flex items-center justify-center mb-4">
              <ChartBarIcon className="h-8 w-8 text-blue-600 mr-3" />
              <h1 className="text-3xl font-bold text-gray-900">Аналитика</h1>
            </div>
            <p className="text-gray-600">Статистика работы и производительности</p>
          </div>

          {/* Информация о пользователе */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <UserIcon className="h-6 w-6 text-blue-600 mr-3" />
                <h2 className="text-xl font-semibold text-gray-900">
                  {isAdmin ? 'Общая аналитика команды' : `Аналитика для ${user?.firstName || user?.username || 'пользователя'}`}
                </h2>
              </div>
              <div className="flex items-center space-x-4">
                <label className="text-sm font-medium text-gray-700">Год:</label>
                <select
                  value={currentYear}
                  onChange={(e) => setCurrentYear(parseInt(e.target.value))}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {generateYearOptions().map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
            {isAdmin && (
              <p className="text-gray-600 mt-2">
                Показана статистика всех пользователей команды
              </p>
            )}
          </div>

          {/* Для администраторов показываем детальную статистику по пользователям */}
          {isAdmin ? (
            <>
              {console.log('Analytics: rendering admin view, usersAnalytics:', usersAnalytics)}
              <UsersAnalyticsTable usersData={usersAnalytics} currentYear={currentYear} />
            </>
          ) : (
            <>
              {console.log('Analytics: rendering user view')}
              {/* Общая статистика для обычных пользователей */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center">
                    <ClockIcon className="h-8 w-8 text-blue-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Общее время</p>
                      <p className="text-2xl font-bold text-gray-900">{formatHours(totalHours)} ч.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center">
                    <CalendarIcon className="h-8 w-8 text-green-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Среднее в месяц</p>
                      <p className="text-2xl font-bold text-gray-900">{averageHoursPerMonth} ч.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center">
                    <ChartBarIcon className="h-8 w-8 text-purple-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Всего задач</p>
                      <p className="text-2xl font-bold text-gray-900">{totalTasks}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center">
                    <div className="h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-green-600 font-bold text-sm">✓</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Завершено</p>
                      <p className="text-2xl font-bold text-gray-900">{totalCompleted}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Простой график для обычных пользователей */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Часы работы по месяцам
                  </h3>
                  <p className="text-gray-600">
                    График показывает количество часов работы и завершенных задач за каждый месяц
                  </p>
                </div>
                
                {totalHours === 0 ? (
                  <div className="h-96 flex items-center justify-center">
                    <div className="text-center">
                      <ChartBarIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Нет данных</h3>
                      <p className="text-gray-500">
                        За {currentYear} год нет завершенных задач в архиве
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Легенда */}
                    <div className="flex items-center justify-center mb-6 space-x-6">
                      <div className="flex items-center">
                        <div className="w-4 h-4 bg-blue-500 rounded mr-2"></div>
                        <span className="text-sm text-gray-600">Часы работы</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
                        <span className="text-sm text-gray-600">Завершенные задачи</span>
                      </div>
                    </div>

                    {/* График */}
                    <div className="grid grid-cols-12 gap-2 h-64">
                      {monthlyHours.map((item, index) => (
                        <div key={index} className="flex flex-col items-center justify-end space-y-2">
                          {/* Столбец часов */}
                          <div className="w-full bg-blue-500 rounded-t transition-all duration-300 relative group"
                            style={{ height: Math.max((item.hours / maxHours) * 100, item.hours > 0 ? 8 : 0) + '%', minHeight: item.hours > 0 ? '16px' : '0' }}>
                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                              {formatHours(item.hours)} ч.
                            </div>
                          </div>
                          {/* Столбец завершенных задач */}
                          <div className="w-full bg-green-500 rounded-t transition-all duration-300 relative group"
                            style={{ height: Math.max((item.completed / maxCompleted) * 100, item.completed > 0 ? 8 : 0) + '%', minHeight: item.completed > 0 ? '16px' : '0' }}>
                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                              {item.completed} задач
                            </div>
                          </div>
                          {/* Название месяца */}
                          <div className="text-xs text-gray-600 text-center">
                            {item.month}
                          </div>
                          {/* Сумма часов за месяц */}
                          <div className="text-xs font-medium text-gray-800 text-center mt-0.5">
                            {formatHours(item.hours)} ч.
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Analytics; 